import type { TradeDetailsDeterministicSummary } from './tradeDetailsDeterministicAdapter';
import type { NormalizedTradeForDetails } from './tradeDetailsNormalization';

export type TradeDetailsPnlMode = 'GROSS' | 'NET';

export type TradeDetailsFinancialSummary = {
  mode: TradeDetailsPnlMode;
  grossPnl: number | null;
  netPnl: number | null;
  selectedPnl: number | null;
  totalFees: number;
  maeGross: number | null;
  maeNet: number | null;
  mfeGross: number | null;
  mfeNet: number | null;
  selectedMae: number | null;
  selectedMfe: number | null;
  selectedBestExit: number | null;
  selectedEfficiencyPct: number | null;
  selectedRiskRewardRatio: number | null;
  excursionAvailability: TradeDetailsDeterministicSummary['availability'];
  excursionSource: 'deterministic_market_bars' | 'deterministic_executions' | 'persisted_trade' | 'none';
  excursionErrorReason?: string;
};

export type TradeExcursionBreakdown = {
  adverseExtreme: number;
  favorableExtreme: number;
  totalRange: number;
  heatSurvived: number;
  capturedProfit: number;
  leftOnTable: number;
  realizedLoss: number;
  recoveredDrawdown: number;
  missedOpportunity: number;
  primaryPercent: number;
};

const round2 = (value: number) => Math.round(value * 100) / 100;

function finiteOrNull(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Resolves all mode-sensitive Trade Details values through one path.
 * Realized P&L is reconstructed from canonical executions whenever they exist;
 * chart bars are consulted only for excursion/best-exit metrics.
 */
export function resolveTradeDetailsFinancialSummary(input: {
  mode: TradeDetailsPnlMode;
  normalizedTrade: NormalizedTradeForDetails;
  deterministicSummary: TradeDetailsDeterministicSummary;
  hasCanonicalExecutions: boolean;
  hasChartBars: boolean;
  preferNormalizedExcursions?: boolean;
  suppressExcursions?: boolean;
}): TradeDetailsFinancialSummary {
  const { normalizedTrade, deterministicSummary } = input;
  const canUseExecutionFinance = input.hasCanonicalExecutions && deterministicSummary.phaseCount > 0;
  const fallbackFees = Math.abs(finiteOrNull(normalizedTrade.fees) ?? 0);
  const totalFees = canUseExecutionFinance
    ? Math.abs(finiteOrNull(deterministicSummary.totalFees) ?? 0)
    : fallbackFees;

  let grossPnl = canUseExecutionFinance
    ? finiteOrNull(deterministicSummary.grossPnl)
    : finiteOrNull(normalizedTrade.grossPnl);
  let netPnl = canUseExecutionFinance
    ? finiteOrNull(deterministicSummary.netPnl)
    : finiteOrNull(normalizedTrade.netPnl);
  if (grossPnl == null && netPnl != null) grossPnl = round2(netPnl + totalFees);
  if (netPnl == null && grossPnl != null) netPnl = round2(grossPnl - totalFees);

  const canUseDeterministicExcursions = !input.suppressExcursions && !input.preferNormalizedExcursions
    && deterministicSummary.phaseCount > 0
    && deterministicSummary.availability === 'available'
    && deterministicSummary.excursionPhaseCount === deterministicSummary.phaseCount;
  const canUsePersistedExcursions = !input.suppressExcursions && (input.preferNormalizedExcursions || !input.hasCanonicalExecutions);
  const phaseCount = Math.max(1, deterministicSummary.phaseCount);
  const maeGross = canUseDeterministicExcursions
    ? finiteOrNull(deterministicSummary.avgPositionMae)
    : canUsePersistedExcursions ? finiteOrNull(normalizedTrade.maeGross) : null;
  const mfeGross = canUseDeterministicExcursions
    ? finiteOrNull(deterministicSummary.avgPositionMfe)
    : canUsePersistedExcursions ? finiteOrNull(normalizedTrade.mfeGross) : null;
  const maeNet = canUseDeterministicExcursions
    ? round2((deterministicSummary.totalMae - totalFees) / phaseCount)
    : canUsePersistedExcursions ? finiteOrNull(normalizedTrade.maeNet) : null;
  const mfeNet = canUseDeterministicExcursions
    ? round2((deterministicSummary.totalMfe - totalFees) / phaseCount)
    : canUsePersistedExcursions ? finiteOrNull(normalizedTrade.mfeNet) : null;

  const selectedPnl = input.mode === 'GROSS' ? grossPnl : netPnl;
  const selectedMae = input.mode === 'GROSS' ? maeGross : maeNet;
  const selectedMfe = input.mode === 'GROSS' ? mfeGross : mfeNet;
  const selectedBestExit = canUseDeterministicExcursions
    ? finiteOrNull(input.mode === 'GROSS' ? deterministicSummary.bestExitGross : deterministicSummary.bestExitNet)
    : selectedMfe;
  const deterministicEfficiency = canUseDeterministicExcursions
    && selectedPnl != null
    && selectedPnl > 0
    && selectedBestExit != null
    && selectedBestExit > 0
    ? finiteOrNull(input.mode === 'GROSS'
      ? deterministicSummary.efficiencyGrossPct
      : deterministicSummary.efficiencyNetPct)
    : null;
  const fallbackEfficiency = selectedPnl != null
    && selectedBestExit != null
    && selectedPnl > 0
    && selectedBestExit > 0
      ? round2((selectedPnl / selectedBestExit) * 100)
      : null;
  const initialRisk = finiteOrNull(normalizedTrade.initialRisk);
  const selectedRiskRewardRatio = initialRisk != null && initialRisk > 0 && selectedPnl != null
    ? selectedPnl / initialRisk
    : finiteOrNull(normalizedTrade.rMultiple);

  return {
    mode: input.mode,
    grossPnl,
    netPnl,
    selectedPnl,
    totalFees,
    maeGross,
    maeNet,
    mfeGross,
    mfeNet,
    selectedMae,
    selectedMfe,
    selectedBestExit,
    selectedEfficiencyPct: deterministicEfficiency ?? fallbackEfficiency,
    selectedRiskRewardRatio,
    excursionAvailability: input.suppressExcursions ? 'unavailable' : canUseDeterministicExcursions || canUsePersistedExcursions ? 'available' : deterministicSummary.availability,
    excursionSource: canUseDeterministicExcursions
      ? deterministicSummary.barExcursionPhaseCount === deterministicSummary.phaseCount ? 'deterministic_market_bars' : 'deterministic_executions'
      : canUsePersistedExcursions ? 'persisted_trade' : 'none',
    excursionErrorReason: input.suppressExcursions ? undefined : deterministicSummary.errorReason,
  };
}

export function calculateTradeExcursionBreakdown(input: {
  pnl: number;
  mae: number | null;
  mfe: number | null;
}): TradeExcursionBreakdown {
  const pnl = finiteOrNull(input.pnl) ?? 0;
  const mae = finiteOrNull(input.mae) ?? 0;
  const mfe = finiteOrNull(input.mfe) ?? 0;
  const adverseExtreme = Math.max(Math.abs(Math.min(0, mae)), Math.abs(Math.min(0, pnl)));
  const favorableExtreme = Math.max(Math.max(0, mfe), Math.max(0, pnl));
  const totalRange = adverseExtreme + favorableExtreme;
  const heatSurvived = round2(Math.max(0, Math.abs(Math.min(0, mae))));
  const capturedProfit = round2(Math.max(0, Math.min(Math.max(0, pnl), favorableExtreme)));
  const leftOnTable = round2(Math.max(0, favorableExtreme - capturedProfit));
  const realizedLoss = round2(Math.max(0, Math.abs(Math.min(0, pnl))));
  const recoveredDrawdown = round2(Math.max(0, adverseExtreme - realizedLoss));
  const missedOpportunity = round2(Math.max(0, mfe));
  const primaryValue = pnl > 0 ? capturedProfit : pnl < 0 ? realizedLoss : 0;
  return {
    adverseExtreme,
    favorableExtreme,
    totalRange,
    heatSurvived,
    capturedProfit,
    leftOnTable,
    realizedLoss,
    recoveredDrawdown,
    missedOpportunity,
    primaryPercent: totalRange > 0 ? Math.round((primaryValue / totalRange) * 100) : 0,
  };
}
